import { createFileRoute } from "@tanstack/react-router";
import {
  isCategoryAvailableForOrderType,
  productsQueryOptions,
} from "@/lib/products";
import { hasDeliveryShiftsQueryOptions } from "@/lib/shifts";
import ProductCard from "@/components/products/product-card";
import { CategoryCarousel } from "@/components/category-carousel";
import { useCartStore } from "@/components/cart/store";
import { useSuspenseMenuQuery } from "@/hooks/use-menu-query";
import { useTranslation } from "react-i18next";
import { useActiveCategory } from "@/hooks/use-active-category";
import { useGetTranslatedOrderType } from "@/hooks/use-get-translated-order-type";
import { useScrollToCategory } from "@/hooks/use-scroll-to-category";
import ProductSearchInput from "@/components/product-search-input";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { OrderTypes } from "@/lib/supabase/enums";

export const Route = createFileRoute("/$locale/menu/")({
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(hasDeliveryShiftsQueryOptions());
    return context.queryClient.ensureQueryData(productsQueryOptions());
  },
  component: MenuPage,
  staleTime: 0,
});

function MenuPage() {
  const productsQuery = useSuspenseMenuQuery();

  const [search, setSearch] = useState("");

  const { t } = useTranslation("translation");
  const { getTranslatedOrderType } = useGetTranslatedOrderType();
  const cart = useCartStore((state) => state.cart);

  // Hooks for carousel functionality
  const scrollToCategory = useScrollToCategory();
  const categoryIds = productsQuery.data.map((category) => category.id);
  const activeCategoryId = useActiveCategory({ categoryIds });

  const handleCategoryClick = (categoryId: string) => {
    scrollToCategory(categoryId);
  };

  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const filteredProducts = productsQuery.data
    .map((category) => ({
      ...category,
      products: category.products.filter((product) =>
        product.name.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((category) => category.products.length > 0);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsHeaderStuck(!entry.isIntersecting);
    });
    const node = sentinelRef.current;
    if (node) observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (!hash || !categoryIds.includes(hash)) return;

    const raf = requestAnimationFrame(() => {
      scrollToCategory(hash);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="py-4">
      <div ref={sentinelRef} />
      <div
        className={cn(
          "sticky top-0 z-20 transition-all",
          isHeaderStuck ? "bg-white drop-shadow" : "bg-transparent shadow-none"
        )}
        data-sticky-header
      >
        <div className="mx-auto max-w-4xl flex flex-col gap-2 px-4 py-2">
          <ProductSearchInput
            onChange={(value) => {
              setSearch(value);
            }}
          />
          {!search && (
            <CategoryCarousel
              categories={productsQuery.data}
              activeCategoryId={activeCategoryId}
              onCategoryClick={handleCategoryClick}
            />
          )}
        </div>
      </div>

      <div id="menu-content" className="max-w-4xl mx-auto">
        <div className="p-4">
          {filteredProducts
            .sort((a, b) => a.position - b.position)
            .map((category) => {
              // Category target restricts which order types the category can
              // be ordered with; it is still shown, but disabled with a note
              const isAvailableForCurrentOrderType =
                isCategoryAvailableForOrderType(category.target, cart.orderType);
              const availableOrderType = !isAvailableForCurrentOrderType
                ? [OrderTypes.delivery, OrderTypes.pickup].find((orderType) =>
                    isCategoryAvailableForOrderType(category.target, orderType)
                  ) ?? null
                : null;

              return (
                <div key={category.id} id={category.id} className="mb-8">
                  <div className="flex flex-row justify-between items-center gap-4 pb-4">
                    <div className="flex flex-col">
                      <h2 className="text-2xl font-bold text-gray-800">
                        {category.name}
                      </h2>
                      {availableOrderType && (
                        <span className="text-sm text-gray-600 italic mt-1">
                          {t("Menu.ProductCategory.only_available_for", {
                            orderType: getTranslatedOrderType(availableOrderType),
                          })}
                        </span>
                      )}
                    </div>
                    {category.isAvailableToday && (
                      <span className="text-sm text-gray-500">
                        {t("Menu.ProductCategory.item_count", {
                          count: category.products.length,
                        })}
                      </span>
                    )}
                  </div>
                  {!category.isAvailableToday ? (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
                      {t("Menu.ProductCategory.unavailable_today")}
                    </p>
                  ) : (
                    <ul>
                      {category.products
                        .sort((a, b) => a.position - b.position)
                        .map((product) => (
                          <li className="not-last:mb-4" key={product.id}>
                            <ProductCard product={product} />
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
